import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import Pagination from "../../Helpers/Pagination";
import { Row, Col, Card, CardBody, Input, Button, Table, Progress } from "reactstrap";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import dayjs from "dayjs";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const VehicleAnalytics = () => {
    const dispatch = useDispatch();
    const [reportData, setReportData] = useState(null);
    const [page, setPage] = useState(1);
    const [filters, setFilters] = useState({
        date_from: "",
        date_to: "",
        search: "",
    });
    const [activeShortcut, setActiveShortcut] = useState('all');

    const handleShortcut = (shortcut) => {
        setActiveShortcut(shortcut);
        let from = "";
        let to = dayjs().format("YYYY-MM-DD");
        
        if (shortcut === 'today') {
            from = dayjs().format("YYYY-MM-DD");
            to = dayjs().format("YYYY-MM-DD");
        } else if (shortcut === 'week') {
            from = dayjs().startOf('week').format("YYYY-MM-DD");
        } else if (shortcut === 'month') {
            from = dayjs().startOf('month').format("YYYY-MM-DD");
        } else if (shortcut === 'year') {
            from = dayjs().startOf('year').format("YYYY-MM-DD");
        } else if (shortcut === 'all') {
            from = "";
            to = "";
        }
        
        setFilters({ ...filters, date_from: from, date_to: to });
    };

    const limit = 20;

    const loadReports = async () => {
        dispatch(loadingTrue());
        let url = `/reports/vehicle_utilization.php?page=${page}&limit=${limit}`;
        if (filters.date_from) url += `&date_from=${filters.date_from}`;
        if (filters.date_to) url += `&date_to=${filters.date_to}`;
        if (filters.search) url += `&search=${filters.search}`;

        const res = await Handler({ method: "get", url });
        if (res?.success) {
            setReportData(res);
        }
        dispatch(loadingFalse());
    };

    useEffect(() => {
        loadReports();
    }, [page, filters.date_from, filters.date_to, filters.search]);

    if (!reportData) return null;

    const summary = reportData?.summary;
    const vehicleChart = reportData?.vehicle_chart;
    const vehicleList = reportData?.data || [];
    const pagination = reportData?.pagination;

    return (
        <div className="p-4 bg-light min-vh-100">
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div>
                    <h4 className="fw-bold text-primary mb-0">Fleet Utilization</h4>
                    <small className="text-muted">Tracking vehicle efficiency and active hours</small>
                </div>
                <div className="d-flex gap-2 align-items-center flex-wrap">
                    <div className="btn-group me-2" role="group">
                        {['all', 'today', 'week', 'month', 'year'].map((shortcut) => (
                            <Button
                                key={shortcut}
                                color={activeShortcut === shortcut ? "primary" : "light"}
                                size="sm"
                                className="rounded-pill px-3 py-1 me-1 border-0"
                                style={{ 
                                    textTransform: 'capitalize', 
                                    fontSize: '11px', 
                                    fontWeight: 'bold',
                                    backgroundColor: activeShortcut === shortcut ? '#0d6efd' : '#fff',
                                    color: activeShortcut === shortcut ? '#fff' : '#6c757d'
                                }}
                                onClick={() => handleShortcut(shortcut)}
                            >
                                {shortcut}
                            </Button>
                        ))}
                    </div>
                    <Input
                        type="date"
                        value={filters.date_from}
                        onChange={(e) => {
                            setFilters({ ...filters, date_from: e.target.value });
                            setActiveShortcut('');
                        }}
                        className="shadow-sm border-0"
                        style={{ width: 140 }}
                    />
                    <Input
                        type="date"
                        value={filters.date_to}
                        onChange={(e) => {
                            setFilters({ ...filters, date_to: e.target.value });
                            setActiveShortcut('');
                        }}
                        className="shadow-sm border-0"
                        style={{ width: 140 }}
                    />
                </div>
            </div>

            {/* SUMMARY CARDS */}
            <Row className="mb-4">
                <Col md={4}>
                    <Card className="border-0 shadow-sm bg-primary text-white">
                        <CardBody className="d-flex align-items-center justify-content-between">
                            <div>
                                <div className="small text-uppercase opacity-75">Total Fleet Hours</div>
                                <h2 className="fw-bold mb-0">{summary?.total_fleet_hours?.toFixed(1)} h</h2>
                            </div>
                            <i className="bi bi-clock-history display-6 opacity-50"></i>
                        </CardBody>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="border-0 shadow-sm">
                        <CardBody className="d-flex align-items-center justify-content-between">
                            <div>
                                <div className="text-muted small text-uppercase">Total Vehicles</div>
                                <h2 className="fw-bold mb-0 text-dark">{pagination?.total_rows}</h2>
                            </div>
                            <div className="bg-info bg-opacity-10 p-3 rounded">
                                <i className="bi bi-truck text-info fs-3"></i>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="border-0 shadow-sm">
                        <CardBody className="d-flex align-items-center justify-content-between">
                            <div>
                                <div className="text-muted small text-uppercase">Avg Utilization</div>
                                <h2 className="fw-bold mb-0 text-success">
                                    {(vehicleList.reduce((acc, curr) => acc + curr.utilization_percent, 0) / (vehicleList.length || 1)).toFixed(1)}%
                                </h2>
                            </div>
                            <div className="bg-success bg-opacity-10 p-3 rounded">
                                <i className="bi bi-pie-chart text-success fs-3"></i>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <Row className="g-4">
                {/* HORIZONTAL BAR CHART */}
                <Col md={12}>
                    <Card className="border-0 shadow-sm">
                        <CardBody>
                            <h6 className="fw-bold mb-4">Vehicle Usage Comparison (Hours)</h6>
                            <div style={{ height: "350px" }}>
                                <Bar
                                    data={{
                                        labels: vehicleChart?.labels || [],
                                        datasets: [{
                                            label: "Total Hours Used",
                                            data: vehicleChart?.datasets?.[0]?.data || [],
                                            backgroundColor: "rgba(13, 110, 253, 0.7)",
                                            borderRadius: 4,
                                        }]
                                    }}
                                    options={{
                                        indexAxis: 'y', // ⭐ Horizontal Bar
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        plugins: { legend: { display: false } }
                                    }}
                                />
                            </div>
                        </CardBody>
                    </Card>
                </Col>

                {/* VEHICLE UTILIZATION TABLE */}
                <Col md={12}>
                    <Card className="border-0 shadow-sm">
                        <CardBody>
                            <h6 className="fw-bold mb-3">Vehicle Detail Report</h6>
                            <Table responsive hover className="align-middle">
                                <thead className="table-light">
                                    <tr>
                                        <th>Vehicle Number</th>
                                        <th>Type</th>
                                        <th className="text-center">Active Days</th>
                                        <th className="text-center">Total Hours</th>
                                        <th width="30%">Utilization Rate</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {vehicleList.length > 0 ? (
                                        vehicleList.map((v) => (
                                            <tr key={v.id}>
                                                <td className="fw-bold">{v.vehicle_number}</td>
                                                <td><span className="badge bg-light text-dark border">{v.lorry_type}</span></td>
                                                <td className="text-center">{v.active_days} days</td>
                                                <td className="text-center fw-bold text-primary">{v.total_hours.toFixed(1)} h</td>
                                                <td>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <div className="flex-grow-1">
                                                            <Progress
                                                                value={v.utilization_percent}
                                                                color={v.utilization_percent > 70 ? "success" : v.utilization_percent > 40 ? "warning" : "danger"}
                                                                style={{ height: '8px' }}
                                                            />
                                                        </div>
                                                        <small className="fw-bold" style={{ minWidth: '45px' }}>{v.utilization_percent}%</small>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr><td colSpan="5" className="text-center py-4">No data found</td></tr>
                                    )}
                                </tbody>
                            </Table>
                            <Pagination
                                page={page}
                                total={pagination?.total_rows || 0}
                                limit={limit}
                                onPrev={() => setPage((p) => Math.max(1, p - 1))}
                                onNext={() => setPage((p) => p + 1)}
                            />
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default VehicleAnalytics;